(() => {
  const selections = new Map();
  const profilesByElement = new WeakMap();
  const pickers = new WeakMap();
  const closing = new WeakMap();

  const initializePicker = (element, select) => {
    const picker = element.querySelector('[data-pv-benefit-picker]');
    const controller = window.PrevuraInsurerPicker?.(picker, select);
    if (!controller) return;
    pickers.set(element, controller);
    element.querySelector('[data-pv-benefit-insurer-label]').htmlFor = picker.querySelector('[data-insurer-trigger]').id;
  };

  const money = (element, cents) => {
    const currency = element.dataset.currency || 'EUR';
    const locale = currency === 'EUR' ? 'de-DE' : element.dataset.locale || 'de-DE';
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency,
      minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const updateEstimate = (element) => {
    const select = element.querySelector('[data-pv-benefit-insurer]');
    const profile = profilesByElement.get(element)?.[select?.value];
    const result = element.querySelector('[data-pv-benefit-result]');
    const unknown = element.querySelector('[data-pv-benefit-unknown]');
    if (!select || !result || !unknown) return;
    const valid = profile && Number.isFinite(profile.rate) && Number.isFinite(profile.cap) && profile.rate >= 0 && profile.cap >= 0;
    result.hidden = !valid;
    unknown.hidden = !select.value || !!valid;
    if (!valid) return;
    const paid = Math.max(0, Number(element.dataset.priceAmount) || 0);
    const refunded = Math.min(paid, Math.round(paid * profile.rate / 100), Math.round(profile.cap * 100), Number(element.dataset.refundCap) || 9900);
    element.querySelector('[data-pv-benefit-estimate]').textContent = money(element, refunded);
    element.querySelector('[data-pv-benefit-own]').textContent = money(element, paid - refunded);
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-pv-price-benefits]').forEach((element) => {
      if (profilesByElement.has(element)) return;
      const source = element.querySelector('[data-pv-benefit-profiles]');
      const select = element.querySelector('[data-pv-benefit-insurer]');
      if (!source || !select) return;
      let profiles;
      try { profiles = JSON.parse(source.textContent); } catch { return; }
      if (!profiles || typeof profiles !== 'object' || Array.isArray(profiles)) return;
      profilesByElement.set(element, profiles);
      const other = select.lastElementChild;
      Object.keys(profiles).sort((a, b) => a.localeCompare(b, element.dataset.locale || 'de')).forEach((name) => {
        const option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        select.insertBefore(option, other);
      });
      const initialValue = selections.get(element.dataset.productId) || document.querySelector('[data-refund-select]')?.value || '';
      select.value = [...select.options].some((option) => option.value === initialValue) ? initialValue : '__other__';
      element.querySelector('[data-pv-benefit-calculator]').hidden = false;
      initializePicker(element, select);
      updateEstimate(element);
    });
  };

  const close = (details, restoreFocus = false, afterClose) => {
    if (!details || !details.open) { afterClose?.(); return; }
    if (closing.has(details)) {
      if (afterClose) closing.get(details).finished.then(afterClose, afterClose);
      return;
    }
    const summary = details.querySelector(':scope > summary');
    const finish = () => {
      pickers.get(details.closest('[data-pv-price-benefits]'))?.close();
      details.open = false;
      details.style.removeProperty('overflow');
      closing.delete(details);
      if (restoreFocus) {
        summary?.focus({ preventScroll: true });
        const bounds = summary?.getBoundingClientRect();
        if (bounds && (bounds.top < 100 || bounds.bottom > window.innerHeight - 24)) {
          summary.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        }
      }
      afterClose?.();
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !details.animate) {
      finish();
      return;
    }
    details.style.overflow = 'hidden';
    const animation = details.animate([
      { height: `${details.getBoundingClientRect().height}px` },
      { height: `${summary.getBoundingClientRect().height}px` },
    ], { duration: 180, easing: 'ease-in-out' });
    closing.set(details, animation);
    animation.onfinish = finish;
    animation.oncancel = finish;
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const closeButton = event.target.closest('[data-pv-benefit-close]');
    if (closeButton) close(closeButton.closest('details'), true);
    const summary = event.target.closest('.pv-price-benefits__details > summary');
    if (summary?.parentElement.open) {
      event.preventDefault();
      close(summary.parentElement, true);
    }
    document.querySelectorAll('[data-pv-price-benefits]').forEach((element) => {
      const picker = pickers.get(element);
      const details = element.querySelector('details[data-pv-light-dismiss]');
      if (picker && !closing.has(details) && event.composedPath().includes(details) && !event.composedPath().includes(picker.element)) picker.close();
    });
    document.querySelectorAll('details[data-pv-light-dismiss][open]').forEach((details) => {
      if (!event.composedPath().includes(details)) close(details);
    });
    const link = event.target.closest('[data-pv-benefit-check-link]');
    if (link) {
      const target = document.getElementById('krankenkassen-check');
      if (target) {
        event.preventDefault();
        close(link.closest('details'), false, () => requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
          const trigger = target.parentElement?.querySelector('[data-refund-custom-trigger]');
          const control = trigger?.getClientRects().length ? trigger : target.parentElement?.querySelector('[data-refund-select]');
          control?.focus({ preventScroll: true });
        }));
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const opened = document.querySelector('details[data-pv-light-dismiss][open]');
    if (!opened) return;
    event.preventDefault();
    close(opened, opened.contains(document.activeElement));
  });

  document.addEventListener('toggle', (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.matches('[data-pv-light-dismiss]')) return;
    if (!opened.open) {
      pickers.get(opened.closest('[data-pv-price-benefits]'))?.close();
      return;
    }
    document.querySelectorAll('details[data-pv-light-dismiss][open]').forEach((details) => {
      if (details !== opened) close(details);
    });
  }, true);

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const select = event.target;
    const inline = select.matches('[data-pv-benefit-insurer]');
    const full = select.matches('[data-refund-select]');
    if (!inline && !full) return;
    const productId = select.closest('[data-pv-price-benefits]')?.dataset.productId;
    document.querySelectorAll('[data-pv-price-benefits]').forEach((element) => {
      if (productId && element.dataset.productId !== productId) return;
      const next = element.querySelector('[data-pv-benefit-insurer]');
      if (!next) return;
      const value = select.value;
      next.value = [...next.options].some((option) => option.value === value) ? value : '__other__';
      selections.set(element.dataset.productId, next.value);
      pickers.get(element)?.sync();
      updateEstimate(element);
    });
    if (inline) document.querySelectorAll('[data-refund-select]').forEach((next) => {
      next.value = [...next.options].some((option) => option.value === select.value) ? select.value : '';
      next.dispatchEvent(new Event('change', { bubbles: false }));
    });
  });

  document.addEventListener('pv:pdp:variant-change', (event) => {
    const { productId, priceAmount } = event.detail || {};
    if (!productId || !Number.isSafeInteger(priceAmount) || priceAmount < 0) return;
    document.querySelectorAll('[data-pv-price-benefits]').forEach((element) => {
      if (element.dataset.productId !== String(productId)) return;
      element.dataset.priceAmount = String(priceAmount);
      element.classList.toggle('pv-price-benefits--long-price', priceAmount % 100 !== 0 || priceAmount >= 10000);
      element.querySelectorAll('[data-pv-benefit-price]').forEach((amount) => { amount.textContent = money(element, priceAmount); });
      const refund = Math.min(priceAmount, Number(element.dataset.refundCap) || 9900);
      element.querySelectorAll('[data-pv-benefit-refund]').forEach((amount) => { amount.textContent = money(element, refund); });
      updateEstimate(element);
    });
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
